'use client'

import { ReactNode, useEffect, useRef } from 'react'
import { useModalStore } from '@/stores/modalStore'

// Definizione di un tipo generico per le props delle modali
export interface ModalComponentProps {
  isOpen: boolean
  onClose: () => void
  [key: string]: unknown
}

type RegisteredModal = {
  id: string
  component: React.ComponentType<ModalComponentProps>
}

/**
 * Componente che gestisce il registro e la visualizzazione delle modali
 */
export const ModalContainer = ({ children }: { children: ReactNode }) => {
  // Registro delle modali disponibili nell'applicazione
  const modalRegistry = useRef<RegisteredModal[]>([])

  // Store delle modali
  const { modalStack, closeModal } = useModalStore()
  const activeModalId =
    modalStack.length > 0 ? modalStack[modalStack.length - 1].id : null
  const activeModalProps =
    modalStack.length > 0 ? modalStack[modalStack.length - 1].props : {}

  // Registra una modale
  const registerModal = (
    id: string,
    component: React.ComponentType<ModalComponentProps>
  ) => {
    if (!modalRegistry.current.some((modal) => modal.id === id)) {
      modalRegistry.current.push({ id, component })
    }
  }

  // Ottiene una modale dal registro
  const getModal = (id: string) => {
    return modalRegistry.current.find((modal) => modal.id === id)
  }

  // Context value da passare ai componenti figli
  const contextValue = {
    registerModal,
    getModal,
  }

  // Renderizza la modale attiva
  const renderActiveModal = () => {
    if (!activeModalId) return null

    const modal = getModal(activeModalId)
    if (!modal) {
      console.error(`Modale con ID "${activeModalId}" non trovata nel registro`)
      return null
    }

    const ModalComponent = modal.component
    return (
      <ModalComponent
        isOpen={true}
        onClose={closeModal}
        {...activeModalProps}
      />
    )
  }

  return (
    <ModalRegistryContext.Provider value={contextValue}>
      {children}
      {renderActiveModal()}
    </ModalRegistryContext.Provider>
  )
}

// Context per il registro delle modali
import { createContext, useContext } from 'react'

type ModalRegistryContextType = {
  registerModal: (
    id: string,
    component: React.ComponentType<ModalComponentProps>
  ) => void
  getModal: (id: string) => RegisteredModal | undefined
}

const ModalRegistryContext = createContext<
  ModalRegistryContextType | undefined
>(undefined)

// Hook per registrare una modale
export const useModalRegistry = () => {
  const context = useContext(ModalRegistryContext)
  if (!context) {
    throw new Error(
      "useModalRegistry deve essere utilizzato all'interno di un ModalContainer"
    )
  }
  return context
}

// Componente HOC per registrare facilmente una modale
export const RegisterModal = ({
  id,
  component,
  children = null,
}: {
  id: string
  component: React.ComponentType<ModalComponentProps>
  children?: ReactNode
}) => {
  const { registerModal } = useModalRegistry()

  useEffect(() => {
    registerModal(id, component)
  }, [id, component, registerModal])

  return <>{children}</>
}
